import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Radio } from 'components';
import { DatePickerButton, TimePicker } from 'design-system-web';
import { H5, H4 } from 'components/typography';
import { ServiceRequestScheduleType } from 'lane-shared/domains/workOrder/workOrderFeatureDefinitions';
import { UserContentInteractionContext } from 'lane-shared/contexts';
import {
  useServiceRequestSchedule,
  SCHEDULE_OPTIONS,
  ScheduleOption,
} from 'react-hooks';

type ServiceRequestScheduleProps = {
  onChange: (schedule: ServiceRequestScheduleType) => void;
  schedule: ServiceRequestScheduleType;
  timeZone?: string;
};

const invalidScheduleSchema = yup.object().shape({
  features: yup.object().shape({
    WorkOrder: yup.object().shape({
      Schedule: yup.object().shape({
        scheduledDueDate: yup
          .date()
          .required('Please select a date and time for your scheduled request')
          .typeError(
            'Please select a date and time for your scheduled request'
          ),
      }),
    }),
  }),
});

export const ServiceRequestSchedule = ({
  onChange,
  schedule,
  timeZone,
}: ServiceRequestScheduleProps) => {
  const { t } = useTranslation();
  const { validateAdditional } = useContext(UserContentInteractionContext);

  const handleScheduleChange = useCallback(
    (scheduledDueDate: Date | null | undefined) =>
      onChange({ ...schedule, scheduledDueDate }),
    [onChange, schedule]
  );

  const {
    scheduleOption,
    selectedDate,
    selectedTime,
    timeSlots,
    minDate,
    isValid,
    handleScheduleOptionChange,
    handleDateChange,
    handleTimeChange,
  } = useServiceRequestSchedule({
    initialScheduledDueDate: schedule?.scheduledDueDate,
    timeZone,
    onChange: handleScheduleChange,
  });

  useEffect(() => {
    if (validateAdditional) {
      validateAdditional(isValid ? null : invalidScheduleSchema);
    }

    return () => {
      if (validateAdditional) {
        validateAdditional(null);
      }
    };
  }, [isValid, validateAdditional]);

  return (
    <div>
      <H4 mb={5}>{t('web.admin.serviceRequest.form.schedule.heading')}</H4>
      <H5 mb={3}>{t('web.admin.serviceRequest.form.schedule.label')}</H5>

      <div>
        <Radio<ScheduleOption>
          key={SCHEDULE_OPTIONS.ASAP}
          value={SCHEDULE_OPTIONS.ASAP}
          name="schedule"
          text={t('web.admin.serviceRequest.form.schedule.asap')}
          selected={scheduleOption}
          onChange={handleScheduleOptionChange}
          testId="asap"
        />
        <Radio<ScheduleOption>
          key={SCHEDULE_OPTIONS.SCHEDULED_DATE}
          value={SCHEDULE_OPTIONS.SCHEDULED_DATE}
          name="schedule"
          text={t('web.admin.serviceRequest.form.schedule.specific-date')}
          selected={scheduleOption}
          onChange={handleScheduleOptionChange}
          testId="specific-date"
        />
      </div>

      {scheduleOption === SCHEDULE_OPTIONS.SCHEDULED_DATE && (
        <div>
          <label data-test="date-requested">
            {t('web.admin.serviceRequest.form.schedule.date-requested')}
          </label>
          <div style={{ marginTop: '10px', display: 'flex', gap: '12px' }}>
            <DatePickerButton
              value={selectedDate}
              onChange={handleDateChange}
              dateInputLabel={t(
                'web.admin.serviceRequest.form.schedule.date-input-label'
              )}
              hideOnSelect
              disablePastDays
              minDate={minDate}
              timeZone={timeZone}
            />
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              times={timeSlots}
              timeZone={timeZone}
              label={t(
                'web.admin.serviceRequest.form.schedule.time-input-label'
              )}
              showClear={false}
              showTimezone={false}
              disabled={!selectedDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
